import { atom } from 'jotai';
import {
  mqttClientAtom,
  mqttConnectionDetailsAtom,
} from '../connection/mqttConnectionAtom';
import { activeTopicsAtom } from '../subscription/subscriptionAtoms';
import { handleMqttMessageAtom } from './messageHandlerAtom';
import { setDebugLabels } from '../utils/atomUtils';
import { mqttLogger } from '@hooks/mqtt/utils';
import {
  createMessageHandler,
  initializeMessageListener,
  cleanupMessageListener,
  incrementListenerRefCount,
  decrementListenerRefCount,
  canInitializeListener,
  canCleanupListener,
} from '../utils/messageUtils';

interface ListenerState {
  isActive: boolean;
  handler: ((topic: string, payload: Buffer) => void) | null;
  refCount: number;
  clientId: string | null;
}

const listenerStateAtom = atom<ListenerState>({
  isActive: false,
  handler: null,
  refCount: 0,
  clientId: null,
});
listenerStateAtom.debugLabel = 'listenerState';

const isListenerActiveAtom = atom((get) => get(listenerStateAtom).isActive);
isListenerActiveAtom.debugLabel = 'isListenerActive';

const handlerReferenceAtom = atom((get) => get(listenerStateAtom).handler);
handlerReferenceAtom.debugLabel = 'handlerReference';

export const listenerRefCountAtom = atom(
  (get) => get(listenerStateAtom).refCount,
);

export const initializeListenerAtom = atom(null, (get, set) => {
  const listenerState = get(listenerStateAtom);
  const client = get(mqttClientAtom);
  const connectionStatus = get(mqttConnectionDetailsAtom);

  if (
    !canInitializeListener(
      listenerState.isActive,
      connectionStatus.isConnected,
      client,
    )
  ) {
    return;
  }

  const clientId = client
    ? (client as any).options?.clientId || 'default'
    : null;

  if (listenerState.isActive && listenerState.clientId === clientId) {
    mqttLogger.debug('Listener already initialized for this client');
    return;
  }

  if (listenerState.handler !== null) {
    mqttLogger.warn('Cleaning up existing handler before re-initialization');
    try {
      client!.removeListener('message', listenerState.handler);
    } catch (error) {
      mqttLogger.debug('Error removing old handler:', error);
    }
  }

  const messageHandler = createMessageHandler(
    () => get(activeTopicsAtom),
    (params) => set(handleMqttMessageAtom, params),
  );

  try {
    initializeMessageListener(client!, messageHandler);

    set(listenerStateAtom, {
      ...listenerState,
      isActive: true,
      handler: messageHandler,
      clientId,
    });

    mqttLogger.info(
      `Message listener successfully initialized for client: ${clientId}`,
    );
  } catch (error) {
    mqttLogger.error('Failed to initialize message listener:', error);
    throw error;
  }
});

export const cleanupListenerAtom = atom(null, (get, set) => {
  const listenerState = get(listenerStateAtom);

  if (!canCleanupListener(listenerState.isActive)) {
    return;
  }

  const client = get(mqttClientAtom);

  cleanupMessageListener(client, listenerState.handler);

  set(listenerStateAtom, {
    ...listenerState,
    isActive: false,
    handler: null,
    clientId: null,
  });
});

export const manageListenerLifecycleAtom = atom(
  null,
  (get, set, action: 'increment' | 'decrement') => {
    const listenerState = get(listenerStateAtom);
    const connectionStatus = get(mqttConnectionDetailsAtom);

    if (action === 'increment') {
      const newCount = incrementListenerRefCount(listenerState.refCount);
      set(listenerStateAtom, {
        ...listenerState,
        refCount: newCount,
      });

      if (
        connectionStatus.isConnected &&
        !listenerState.isActive &&
        newCount === 1
      ) {
        set(initializeListenerAtom);
      }
    } else if (action === 'decrement') {
      const newCount = decrementListenerRefCount(listenerState.refCount);
      set(listenerStateAtom, {
        ...listenerState,
        refCount: newCount,
      });
    }
  },
);

export const listenerStatusAtom = atom((get) => ({
  isActive: get(isListenerActiveAtom),
  refCount: get(listenerRefCountAtom),
  hasHandler: get(handlerReferenceAtom) !== null,
}));

export const connectionListenerAtom = atom(null, (get, set) => {
  const connectionStatus = get(mqttConnectionDetailsAtom);
  const listenerState = get(listenerStateAtom);
  const client = get(mqttClientAtom);

  if (connectionStatus.isConnected && !listenerState.isActive && client) {
    mqttLogger.info('Connection established, initializing message listener');
    set(initializeListenerAtom);
  } else if (!connectionStatus.isConnected && listenerState.isActive) {
    mqttLogger.info('Connection lost, cleaning up listener');
    const client = get(mqttClientAtom);

    if (listenerState.handler && client) {
      try {
        client.removeListener('message', listenerState.handler);
      } catch (error) {
        mqttLogger.warn('Error removing listener during disconnect:', error);
      }
    }

    set(listenerStateAtom, {
      isActive: false,
      handler: null,
      refCount: listenerState.refCount,
      clientId: null,
    });
  }
});

setDebugLabels({
  listenerState: listenerStateAtom,
  initializeListener: initializeListenerAtom,
  cleanupListener: cleanupListenerAtom,
  manageListenerLifecycle: manageListenerLifecycleAtom,
  listenerStatus: listenerStatusAtom,
  isListenerActive: isListenerActiveAtom,
  handlerReference: handlerReferenceAtom,
  listenerRefCount: listenerRefCountAtom,
  connectionListener: connectionListenerAtom,
});
